import { NavLink } from 'react-router'
import type { ComponentType } from 'react'
import {
  CalendarIcon,
  HomeIcon,
  ProfileIcon,
  TrainingIcon,
} from './icons/TabBarIcons'
import { useI18n, type MessageKey } from '../i18n'

type TabIcon = ComponentType<{
  active: boolean
  className?: string
}>

type TabItem = {
  labelKey: MessageKey
  to: string
  end?: boolean
  Icon: TabIcon
  /**
   * Renders the tab greyed out and inert — visible in the bar, but not a link
   * and not focusable. For tabs whose destination isn't built yet.
   */
  comingSoon?: boolean
}

const SAFE_BOTTOM = 'env(safe-area-inset-bottom, 0px)'

/**
 * All four tabs from the design are present. Calendar and Training are marked
 * `comingSoon` for phase 1: they show in the bar so the footer keeps its final
 * shape (and users can see what's on the way), but they don't navigate. They're
 * slated to be linked in September 2026 — at which point the fix is to drop the
 * `comingSoon` flag here and add the matching route in App.tsx. Nothing else in
 * the bar needs to change, and no re-layout is involved.
 */
const tabItems: TabItem[] = [
  { labelKey: 'nav.home', to: '/home', Icon: HomeIcon },
  { labelKey: 'nav.calendar', to: '/calendar', Icon: CalendarIcon, comingSoon: import.meta.env.VITE_MEETINGS_ENABLED !== 'true' },
  { labelKey: 'nav.training', to: '/training', Icon: TrainingIcon, comingSoon: import.meta.env.VITE_MEETINGS_ENABLED !== 'true' },
  { labelKey: 'nav.profile', to: '/profile', Icon: ProfileIcon },
]

/** Shared by both the live and greyed-out tabs so they stay geometrically identical. */
const TAB_LAYOUT =
  'flex min-w-0 flex-col items-center justify-center gap-1 text-caption font-semibold'

/**
 * Greyed-out treatment. gray-600 is already the *inactive* tab color, so a
 * disabled tab needs to sit a step below it or the two read the same — hence the
 * opacity rather than another gray token. Turn this up/down to taste; it's the
 * only knob controlling how faint the phase-2 tabs look.
 */
const COMING_SOON_CLASS = 'text-gray-600 opacity-60 select-none'

export function TabBar() {
  const { t } = useI18n()

  return (
    <nav
      aria-label={t('nav.primary')}
      className="fixed inset-x-0 bottom-0 z-50 border-t border-gray-500 bg-white"
      style={{ paddingBottom: SAFE_BOTTOM }}
    >
      {/* Column count is inline rather than a `grid-cols-N` class: Tailwind only
          emits classes it can see as literal strings, so a computed one wouldn't
          exist in the CSS. This keeps the tabs evenly divided at any count. */}
      <div
        className="mx-auto grid h-16 max-w-screen-sm md:max-w-4xl"
        style={{ gridTemplateColumns: `repeat(${tabItems.length}, minmax(0, 1fr))` }}
      >
        {tabItems.map(({ labelKey, to, end, Icon, comingSoon }) =>
          comingSoon ? (
            // A <span>, not a disabled <NavLink> — react-router has no disabled
            // state, and a link with a suppressed onClick is still focusable,
            // still shows a URL on hover, and is still followable by keyboard.
            // Not being an anchor at all is what actually makes it unclickable.
            <span
              key={to}
              aria-disabled="true"
              className={`${TAB_LAYOUT} ${COMING_SOON_CLASS}`}
            >
              <span className="flex size-6 shrink-0 items-center justify-center">
                <Icon active={false} />
              </span>
              <span className="w-full truncate text-center">{t(labelKey)}</span>
            </span>
          ) : (
            <NavLink
              key={to}
              to={to}
              end={end}
              className={({ isActive }) =>
                [
                  TAB_LAYOUT,
                  'transition-colors',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-inset',
                  isActive ? 'text-primary' : 'text-gray-600',
                ].join(' ')
              }
            >
              {({ isActive }) => (
                <>
                  <span className="flex size-6 shrink-0 items-center justify-center">
                    <Icon active={isActive} />
                  </span>
                  <span className="w-full truncate text-center">{t(labelKey)}</span>
                </>
              )}
            </NavLink>
          ),
        )}
      </div>
    </nav>
  )
}
